import React, { type FC, useRef, useState } from 'react';

import ValuationsContainer from './valuations/ValuationsContainer';
import RepairCosts from './RepairCosts';
import ApproachSelect from './approach-select/ApproachSelect';
import DecisionMakingPropertyHeader from './DecisionMakingPropertyHeader';

import { enableMapSet } from 'immer';
import QuickNetSheetModal from './quick-net-sheet/QuickNetSheetModal';
import PropertyCommentsModal from '../common/modals/property-comments/PropertyCommentsModal';
import { useDecisionFormContext } from '../../contexts/DecisionMaking/DecisionFormContext';
import { usePropertyContext } from '../../contexts/DecisionMaking/PropertyContext';
import DecisionMakingCompsContainer from './DecisionMakingCompsContainer';
import DecisionMakingPropertyContainer from './DecisionMakingPropertyContainer';
import { Toast } from 'primereact/toast';
import DecisionMakingWantedStatusModal from './DecisionMakingWantedStatusModal';
import ConfirmDecisionModal from './ConfirmDecisionModal';

enableMapSet();

export type DecisionMakingContainerProps = {
}

const DecisionMakingContainer: FC<DecisionMakingContainerProps> = ({}) => {
  const toast = useRef(null);
  const [ isQuickNetModalOpen, setIsQuickNetModalOpen ] = useState(false);
  const [ isPropertyCommentsModalOpen, setIsPropertyCommentsModalOpen ] = useState(false);
  const [ isWantedStatusModalOpen, setIsWantedStatusModalOpen ] = useState(false);
  const [ isConfirmDecisionModalOpen, setIsConfirmDecisionModalOpen ] = useState(false);

  const {
    state: {
      wanted,
      formUpdated,
      valuationType,
      approachId,
      fixedCosts,
      estimatedValue,
      estimatedValuePerSqft,
    },
    dispatch,
  } = useDecisionFormContext();

  const {
    propertyId,
  } = usePropertyContext();

  const displayConfirmationToast = (success: boolean) => {
    if (success) {
      toast.current.show({ severity: 'success', summary: 'Confirmed', detail: 'Decision has been saved', life: 3000 });
    } else {
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'Decision has not been saved', life: 3000 });
    }
  };

  // This is only changing it for the UI. The api call is made on "submit decision"
  const onWantedStatusChange = (wantedStatus: boolean) => {
    if (wantedStatus !== wanted) {
      dispatch({ type: 'SET_FORM_FIELD',
        payload: {
          field: 'wanted',
          value: wantedStatus
        }
      });
    }
  };

  const handleSubmitDecision = () => {

    if (!propertyId) {
      return null;
    }
    if (!wanted) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Must select a valid wanted status to save a decision.'
      });
    }
    if (!approachId) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Approach must be selected to save a decision.'
      });
    }
    if (!valuationType) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Valuation type must be selected to save a decision.'
      });
    }
    if (!estimatedValue || !estimatedValuePerSqft) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Valuation values must be set to save a decision.'
      });
    }
    if (fixedCosts === null) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Fixed costs must be set to save a decision.'
      });
    }
    if (wanted && approachId && valuationType && fixedCosts!==null) {
      setIsConfirmDecisionModalOpen(true);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <div id='DecisionMakingContainer'>
        <div>
          {propertyId ?
            <DecisionMakingPropertyHeader
              propertyId={propertyId}
              setIsQuickNetModalOpen={setIsQuickNetModalOpen}
              setIsPropertyCommentsModalOpen={setIsPropertyCommentsModalOpen}
              savedWantedStatus={wanted}
              onWantedStatusChange={onWantedStatusChange}
              handleSubmitDecision={handleSubmitDecision}
              formUpdated={formUpdated}
            /> :
            <div className="m-4"/>
          }
          <div id='DecisionMakingContainer-container' className={'max-h-full top-0 pb-10 text-xs '}>
            <div className='grid lg:grid-cols-3 md:grid-cols-1 sm:grid-cols-1 gap-x-3 rounded-xl sm:text-center h-full'>
              <div className='h-full flex flex-col'>
                <div id='valuations-container' className='rounded-xl shadow-2xl h-contain overflow-y-scroll'>
                  <ValuationsContainer />
                </div>
              </div>
              <div className='w-full h-fill'>
                <div id='approach-select-container' className='rounded-xl shadow-2xl h-contain'>
                  <ApproachSelect />
                </div>
                <div id='repair-costs-container' className='rounded-xl shadow-2xl h-contain'>
                  <RepairCosts />
                </div>
              </div>
              <DecisionMakingPropertyContainer />
            </div>
          </div>
          {isPropertyCommentsModalOpen &&
          <PropertyCommentsModal
            isOpen={isPropertyCommentsModalOpen}
            propertyId={propertyId}
            setIsPropertyCommentsModalOpen={setIsPropertyCommentsModalOpen}
          />
          }
          {isQuickNetModalOpen &&
          <QuickNetSheetModal
            isOpen={isQuickNetModalOpen}
            setModalOpen={setIsQuickNetModalOpen}
          />
          }
          {isWantedStatusModalOpen &&
          <DecisionMakingWantedStatusModal
            isOpen={isWantedStatusModalOpen}
            setModalOpen={setIsWantedStatusModalOpen}
          />
          }
          {isConfirmDecisionModalOpen &&
          <ConfirmDecisionModal
            isOpen={isConfirmDecisionModalOpen}
            setModalOpen={setIsConfirmDecisionModalOpen}
            onSuccess={displayConfirmationToast}
          />
          }
        </div>
        <DecisionMakingCompsContainer />
      </div>
    </>
  );
};

export default DecisionMakingContainer;
